import { Controller, useFormContext } from "react-hook-form";

import { TextArea } from "ui/text-area";
import StrategyLink from "components/modals/shared/strategy-link";
import { Button } from "ui/button";
import { DialogActions, DialogBackButton } from "ui/dialog";

import { useAddCreativesWithWriterContext } from "components/modals/add-creatives-with-writer-modal/context/add-creatives-with-writer-context";
import { useAddCreativesWithWriter } from "components/modals/add-creatives-with-writer-modal/hooks/use-add-creatives-with-writer";
import type { AddCreativesWithWriterFormData } from "components/modals/add-creatives-with-writer-modal/add-creatives-with-writer-modal.types";

import { getProgramTypeLabel } from "containers/admin/clients/client/campaign-templates-page/campaign-template.constants";
import type { CampaignTemplateProgram } from "containers/admin/clients/client/campaign-templates-page/campaign-templates-page.types";

import { useRequiredApiContext } from "hooks/api/helpers/api-context";
import { useCurrentCampaign } from "hooks/api/use-campaigns";

import { getCampaignPaths, getClientPaths } from "utilities/app-routes";

export const AdditionalInstructionsStep = () => {
  const { control } = useFormContext<AddCreativesWithWriterFormData>();
  const { clientId, campaignId } = useRequiredApiContext();
  const campaign = useCurrentCampaign();

  const { clientProgramsA2APath, clientProgramsA2EPath } = getClientPaths({ clientId });
  const { campaignStrategyPath } = getCampaignPaths({ clientId, campaignId });
  const programStrategyHref =
    campaign.programType === "ADMIT_TO_ENROLL" ? clientProgramsA2EPath : clientProgramsA2APath;
  const programStrategyLabel = campaign.programType
    ? (getProgramTypeLabel(campaign.programType as CampaignTemplateProgram) as string)
    : "Program strategy";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 text-gray-900">
        <p>
          Apollo generates the creatives based on client program strategy. To tailor tone,
          content, or structure, you can edit the program strategy for this client.
        </p>
        <p>Add any additional instructions if needed for these specific creatives.</p>
      </div>

      <Controller
        name="additionalInstructions"
        control={control}
        render={({ field: { onChange, value, ...field } }) => (
          <TextArea
            label={<span className="font-bold">Additional instructions</span>}
            value={value || ""}
            onChange={(event) => onChange(event.target.value)}
            rows={5}
            resize="none"
            {...field}
          />
        )}
      />

      <div className="flex flex-col gap-2">
        <StrategyLink
          label="Program strategy:"
          linkLabel={programStrategyLabel}
          href={programStrategyHref}
        />
        <StrategyLink
          label="Campaign strategy:"
          linkLabel={campaign.name}
          href={campaignStrategyPath}
        />
      </div>
    </div>
  );
};

export const AdditionalInstructionsStepActions = () => {
  const { back: handleBack } = useAddCreativesWithWriterContext();
  const { handleAddCreatives, isSubmitting } = useAddCreativesWithWriter();

  return (
    <DialogActions
      type="steps"
      back={
          <DialogBackButton onClick={handleBack} disabled={isSubmitting}>Back to details</DialogBackButton>
        }
      primary={
          <Button size="large" loading={isSubmitting} onClick={handleAddCreatives}>
            Add creatives
          </Button>
        }
    />
  );
};
